import React from "react";
import { useNavigate } from "react-router-dom";
import PageShell from "@/components/PageShell";
import { UserCheck, Building2, TrendingUp, Shield, Award } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { t } from "@/i18n";

export default function Network() {
  const { user, lang } = useApp();
  const navigate = useNavigate();
  const cta = () => navigate(user ? "/dashboard" : "/login");

  const pillars = [
    { icon: Shield, title: t("network_pillar1_t", lang), desc: t("network_pillar1_d", lang) },
    { icon: Award, title: t("network_pillar2_t", lang), desc: t("network_pillar2_d", lang) },
    { icon: TrendingUp, title: t("network_pillar3_t", lang), desc: t("network_pillar3_d", lang) },
  ];
  const profiles = [
    { icon: Building2, title: t("network_p_agent_t", lang), desc: t("network_p_agent_d", lang) },
    { icon: UserCheck, title: t("network_p_owner_t", lang), desc: t("network_p_owner_d", lang) },
    { icon: Award, title: t("network_p_buyer_t", lang), desc: t("network_p_buyer_d", lang) },
  ];
  const steps = [
    { step: "01", title: t("network_step1_t", lang), desc: t("network_step1_d", lang) },
    { step: "02", title: t("network_step2_t", lang), desc: t("network_step2_d", lang) },
    { step: "03", title: t("network_step3_t", lang), desc: t("network_step3_d", lang) },
  ];

  return (
    <PageShell
      testid="network-page"
      eyebrow={t("network_eyebrow", lang)}
      title={t("network_title", lang)}
      intro={t("network_intro", lang)}
    >
      <div className="grid gap-px bg-[#D8CFBB] md:grid-cols-3 mb-16" data-testid="network-pillars">
        {pillars.map((p) => (
          <div key={p.title} className="bg-[#FFFAF0] p-8 lg:p-10">
            <p.icon className="h-5 w-5 text-[#B8965A] mb-6" />
            <h3 className="text-2xl font-light mb-3 text-[#1A1610]" style={{ fontFamily: "Cormorant Garamond, serif" }}>{p.title}</h3>
            <p className="text-sm text-[#6B5F4C] leading-relaxed">{p.desc}</p>
          </div>
        ))}
      </div>

      <div className="mb-16">
        <p className="text-[10px] tracking-[0.3em] uppercase text-[#B8965A] mb-3">{t("network_profiles_eyebrow", lang)}</p>
        <h2 className="text-3xl sm:text-4xl font-light text-[#1A1610] mb-10" style={{ fontFamily: "Cormorant Garamond, serif" }}>
          {t("network_profiles_title", lang)}
        </h2>
        <div className="grid gap-4 md:grid-cols-3" data-testid="network-profiles">
          {profiles.map((r, i) => (
            <div key={r.title} className="border border-[#D8CFBB] rounded-lg p-7 bg-[#FFFAF0] hover:border-[#B8965A] hover:-translate-y-1 transition-transform duration-300">
              <p className="text-[10px] tracking-[0.3em] uppercase text-[#B8965A] mb-4">{t("network_profiles_eyebrow", lang)} · 0{i+1}</p>
              <r.icon className="h-5 w-5 text-[#B8965A] mb-3" />
              <h4 className="text-xl font-light mb-2 text-[#1A1610]" style={{ fontFamily: "Cormorant Garamond, serif" }}>{r.title}</h4>
              <p className="text-sm text-[#6B5F4C] leading-relaxed">{r.desc}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="mb-16">
        <p className="text-[10px] tracking-[0.3em] uppercase text-[#B8965A] mb-3">{t("network_how_eyebrow", lang)}</p>
        <h2 className="text-3xl sm:text-4xl font-light text-[#1A1610] mb-10" style={{ fontFamily: "Cormorant Garamond, serif" }}>
          {t("network_how_title", lang)}
        </h2>
        <ol className="space-y-6" data-testid="network-steps">
          {steps.map((h) => (
            <li key={h.step} className="grid grid-cols-[80px_1fr] gap-6 items-start border-b border-[#E6DFCF] pb-6 last:border-b-0">
              <span className="text-4xl font-light text-[#B8965A]" style={{ fontFamily: "Cormorant Garamond, serif" }}>{h.step}</span>
              <div>
                <h4 className="text-xl font-light text-[#1A1610] mb-1" style={{ fontFamily: "Cormorant Garamond, serif" }}>{h.title}</h4>
                <p className="text-sm text-[#4A3F30] leading-relaxed">{h.desc}</p>
              </div>
            </li>
          ))}
        </ol>
      </div>

      <div className="border border-[#B8965A]/40 bg-gradient-to-r from-[#B8965A]/5 via-transparent to-[#B8965A]/5 rounded-lg p-10 lg:p-12 text-center">
        <p className="text-[10px] tracking-[0.4em] uppercase text-[#B8965A] mb-3">{t("network_cta_eyebrow", lang)}</p>
        <h3 className="text-3xl sm:text-4xl font-light mb-6" style={{ fontFamily: "Cormorant Garamond, serif" }}>
          {t("network_cta_title", lang)}
        </h3>
        <button
          onClick={cta}
          data-testid="network-cta"
          className="bg-[#B8965A] text-white hover:bg-[#a08248] tracking-[0.25em] uppercase text-xs px-10 h-14 transition-colors duration-200"
        >
          {user ? t("network_cta_button_dashboard", lang) : t("network_cta_button_new", lang)}
        </button>
      </div>
    </PageShell>
  );
}
